import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Check, Star, Truck, ShieldCheck, ArrowLeft } from "lucide-react";
import { api, formatINR, formatApiErr } from "@/lib/api";
import { toast } from "sonner";

const BOOK_COVER = "https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/h1hp4nil_0e90368f-4448-46e1-9584-5bc3a35607eb.JPG";

const VARIANTS = [
    { id: "paperback", label: "Paperback",  priceInr: 950,  tag: "Best value" },
    { id: "hardcover", label: "Hardcover",  priceInr: 1150, tag: "Premium gift" },
];

const HIGHLIGHTS = [
    "All 9 grahas, 12 bhavas & 27 nakshatras — what each significates",
    "Karakatva system: how planets, signs & houses inter-signify",
    "Practical chart-reading methodology with case examples",
    "Companion glossary, references & student-friendly diagrams",
];

export default function Book() {
    const [variant, setVariant] = useState("paperback");
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", phone: "",
        address_line1: "", address_line2: "",
        city: "", state: "", postal_code: "", country: "India",
    });
    const selected = VARIANTS.find((v) => v.id === variant);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post("/payments/book-checkout", {
                variant,
                ...form,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (err) {
            toast.error(formatApiErr(err.response?.data?.detail) || "Could not start checkout");
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="book-page" className="pt-32 pb-20 min-h-screen">
            <div className="container-vv">
                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    {/* Cover */}
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-6 bg-gold/10 blur-3xl rounded-full" />
                            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-gold/30 glow-gold bg-cream">
                                <img src={BOOK_COVER} alt="The Complete Guide to Karakatva in Vedic Astrology" className="w-full h-full object-contain" data-testid="book-cover" />
                            </div>
                            <div className="absolute -bottom-4 -right-4 bg-ink-surface/90 backdrop-blur-md border border-gold/30 rounded-xl px-4 py-3 flex items-center gap-2">
                                {[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />)}
                                <span className="text-xs text-cream-muted">Author&apos;s edition</span>
                            </div>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="lg:col-span-7">
                        <div className="label-eyebrow flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> Now Available</div>
                        <h1 className="font-serif text-4xl sm:text-5xl text-cream mt-4 leading-tight text-balance">
                            The Complete Guide to <span className="italic text-gold">Karakatva</span> in Vedic Astrology
                        </h1>
                        <div className="mt-4 text-cream-muted text-lg">by <span className="text-cream font-medium">Tanu Aggarwal</span></div>

                        <p className="mt-6 text-cream-muted leading-relaxed">
                            A definitive student-and-practitioner reference on <em>karakatva</em> — the doctrine of significations that sits at the heart of every accurate Vedic reading. Written from 9+ years of one-on-one consultation experience and classical study, this book turns scattered Jyotish notes into a coherent, working method.
                        </p>

                        <div className="mt-8 grid sm:grid-cols-2 gap-3">
                            {HIGHLIGHTS.map((h) => (
                                <div key={h} className="flex items-start gap-2 text-sm text-cream">
                                    <Check className="h-4 w-4 text-gold flex-shrink-0 mt-0.5" />
                                    <span>{h}</span>
                                </div>
                            ))}
                        </div>

                        {/* Variant picker */}
                        <div className="mt-10 grid sm:grid-cols-2 gap-4">
                            {VARIANTS.map((v) => {
                                const active = variant === v.id;
                                return (
                                    <button
                                        key={v.id}
                                        type="button"
                                        onClick={() => setVariant(v.id)}
                                        data-testid={`variant-${v.id}`}
                                        className={`text-left rounded-xl border p-5 transition-all ${
                                            active ? "border-gold bg-gold/10" : "border-gold/15 bg-ink-deep/40 hover:border-gold/40"
                                        }`}
                                    >
                                        <div className="flex items-start justify-between mb-2">
                                            <div>
                                                <div className="font-serif text-xl text-cream">{v.label}</div>
                                                <div className="text-xs text-gold mt-0.5">{v.tag}</div>
                                            </div>
                                            {active && <Check className="h-5 w-5 text-gold" />}
                                        </div>
                                        <div className="font-serif text-2xl text-gold mt-3">{formatINR(v.priceInr)}</div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex items-center gap-6 text-xs text-cream-muted">
                            <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-gold" /> India-wide shipping</div>
                            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-gold" /> Secure Stripe checkout</div>
                        </div>

                        {!showForm ? (
                            <button onClick={() => setShowForm(true)} className="btn-gold mt-8" data-testid="proceed-to-checkout">
                                Buy {selected.label} — {formatINR(selected.priceInr)} <ArrowRight className="h-4 w-4" />
                            </button>
                        ) : null}
                    </div>
                </div>

                {/* Shipping form */}
                {showForm && (
                    <form onSubmit={submit} className="card-luxe mt-12 max-w-3xl mx-auto" data-testid="book-checkout-form">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="font-serif text-2xl text-cream">Shipping details</h2>
                            <button type="button" onClick={() => setShowForm(false)} className="text-xs text-cream-muted hover:text-gold flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Back</button>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Full name *</label>
                                <input className="input-luxe mt-2" required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="book-name" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Email *</label>
                                <input type="email" className="input-luxe mt-2" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="book-email" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Phone *</label>
                                <input className="input-luxe mt-2" required value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="book-phone" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Pincode *</label>
                                <input className="input-luxe mt-2" required value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} data-testid="book-pincode" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Address line 1 *</label>
                                <input className="input-luxe mt-2" required value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} data-testid="book-address1" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-xs tracking-widest uppercase text-cream-muted">Address line 2</label>
                                <input className="input-luxe mt-2" value={form.address_line2} onChange={(e) => update("address_line2", e.target.value)} data-testid="book-address2" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">City *</label>
                                <input className="input-luxe mt-2" required value={form.city} onChange={(e) => update("city", e.target.value)} data-testid="book-city" />
                            </div>
                            <div>
                                <label className="text-xs tracking-widest uppercase text-cream-muted">State *</label>
                                <input className="input-luxe mt-2" required value={form.state} onChange={(e) => update("state", e.target.value)} data-testid="book-state" />
                            </div>
                        </div>

                        <div className="mt-8 pt-6 border-t border-gold/15 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="text-xs tracking-widest uppercase text-cream-muted">Order Summary</div>
                                <div className="font-serif text-lg text-cream mt-1">Karakatva — {selected.label}</div>
                                <div className="font-serif text-2xl text-gold mt-1">{formatINR(selected.priceInr)}</div>
                            </div>
                            <button type="submit" disabled={submitting} className="btn-gold disabled:opacity-50" data-testid="book-pay-btn">
                                {submitting ? "Redirecting..." : <>Pay & Order <ArrowRight className="h-4 w-4" /></>}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
